import {
  type AddressBookList,
  type AiMailRequest,
  type AiMailResult,
  type AliasCreateRequest,
  type AuditList,
  type AuditQuery,
  type AuthSession,
  addressBookListSchema,
  aiMailResultSchema,
  appPasswordSchema,
  auditListSchema,
  authSessionSchema,
  type BillingPlanRequest,
  type BillingRedirect,
  type BillingSummary,
  billingRedirectSchema,
  billingSummarySchema,
  type CalendarEvent,
  type CalendarEventInput,
  type CalendarEventList,
  type CalendarEventPatch,
  type CalendarList,
  type Contact,
  type ContactInput,
  type ContactList,
  type ContactPatch,
  calendarEventListSchema,
  calendarEventSchema,
  calendarListSchema,
  contactListSchema,
  contactSchema,
  type DomainAuthCode,
  type DomainCheckRequest,
  type DomainCheckResponse,
  type DomainRenewQuote,
  type DomainSettingsRequest,
  type DomainVerification,
  domainAuthCodeSchema,
  domainCheckResponseSchema,
  domainRenewQuoteSchema,
  domainVerificationSchema,
  type EmailBatchUpdateRequest,
  type EmailDetail,
  type EmailDnsCheck,
  type EmailPage,
  type EmailSetupApplyResponse,
  type EmailUpdateRequest,
  type Entitlements,
  emailDetailSchema,
  emailDnsCheckSchema,
  emailPageSchema,
  emailSetupApplyResponseSchema,
  entitlementsSchema,
  type Features,
  featuresSchema,
  type InviteAcceptRequest,
  type InviteCreated,
  type InviteCreateRequest,
  type InviteList,
  type InvitePreview,
  inviteCreatedSchema,
  inviteListSchema,
  invitePreviewSchema,
  type LoginRequest,
  type MailAccount,
  type Mailbox,
  type MailboxCreated,
  type MailboxCreateRequest,
  type MailboxList,
  type MailClientSettings,
  type MailFolderList,
  type MailGroup,
  type MailGroupCreateRequest,
  type MailGroupList,
  type MailGroupUpdateRequest,
  type MailSettings,
  type MfaVerifyRequest,
  mailAccountSchema,
  mailboxCreatedSchema,
  mailboxListSchema,
  mailboxSchema,
  mailClientSettingsSchema,
  mailFolderListSchema,
  mailGroupListSchema,
  mailGroupSchema,
  mailSettingsSchema,
  type Note,
  type Notebook,
  type NotebookList,
  type NoteInput,
  type NoteList,
  type NotePatch,
  type NotePermission,
  type NoteQuery,
  type NoteTagList,
  notebookListSchema,
  notebookSchema,
  noteListSchema,
  noteSchema,
  noteShareSchema,
  noteTagListSchema,
  type Order,
  type OrderCreated,
  type OrderCreateRequest,
  type OrderList,
  type OrderQuote,
  type OrderQuoteRequest,
  type OwnedDomain,
  type OwnedDomainDetail,
  type OwnedDomainList,
  orderCreatedSchema,
  orderListSchema,
  orderQuoteSchema,
  orderSchema,
  ownedDomainDetailSchema,
  ownedDomainListSchema,
  ownedDomainSchema,
  type PasskeyList,
  type PasskeySummary,
  type PushConfig,
  type PushSubscriptionInput,
  passkeyListSchema,
  passkeySummarySchema,
  peopleSchema,
  pushConfigSchema,
  pushDevicesSchema,
  pushTestResultSchema,
  type RecipientSuggestionList,
  recipientSuggestionListSchema,
  type SaveDraftRequest,
  type SendEmailRequest,
  type SentEmail,
  type SessionList,
  type SignupRequest,
  savedDraftSchema,
  sentEmailSchema,
  sessionListSchema,
  type Task,
  type TaskInput,
  type TaskList,
  type TaskListsResponse,
  type TaskPatch,
  type TasksResponse,
  type TotpConfirmResponse,
  type TotpDisableRequest,
  type TotpEnrollResponse,
  taskListSchema,
  taskListsResponseSchema,
  taskSchema,
  tasksResponseSchema,
  totpConfirmResponseSchema,
  totpEnrollResponseSchema,
  type UploadedAttachment,
  type UserList,
  type UserRole,
  undoSendResultSchema,
  uploadedAttachmentSchema,
  userListSchema,
  type WebauthnOptions,
  type WebauthnResponse,
  type WorkspaceClosed,
  type WorkspaceCloseRequest,
  webauthnOptionsSchema,
  workspaceClosedSchema,
} from "@websede/contracts";
import { z } from "zod";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** error.details from the API, for the few errors the client acts on. */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

const okSchema = z.object({ ok: z.literal(true) });

/**
 * CSRF token of the current session, taken from every session payload the API returns and sent
 * back on state-changing requests. Lives in memory only: a reload fetches /me and refills it.
 */
let csrfToken: string | null = null;

function parseSession(data: unknown): AuthSession {
  const session = authSessionSchema.parse(data);
  csrfToken = session.session.csrfToken;
  return session;
}

export function forgetSession(): void {
  csrfToken = null;
}

async function request<TResponse>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  parse: (data: unknown) => TResponse,
  body?: unknown,
): Promise<TResponse> {
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["content-type"] = "application/json";
  }
  if (method !== "GET" && csrfToken) {
    headers["x-csrf-token"] = csrfToken;
  }
  const init: RequestInit = { method, credentials: "include", headers };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, init);
  } catch {
    // The browser could not reach the server at all: offline, DNS, or the connection dropped.
    throw new ApiRequestError(0, "offline", "The server could not be reached");
  }
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (
      data as {
        error?: { code?: string; message?: string; details?: Record<string, unknown> };
      } | null
    )?.error;
    // A gateway answering for an API it cannot reach is the same situation for the reader.
    const unreachable = !error && [502, 503, 504].includes(response.status);
    throw new ApiRequestError(
      response.status,
      error?.code ?? (unreachable ? "offline" : "request_failed"),
      error?.message ?? response.statusText,
      error?.details,
    );
  }
  return parse(data);
}

const get = <T>(path: string, parse: (d: unknown) => T) => request("GET", path, parse);
const post = <T>(path: string, body: unknown, parse: (d: unknown) => T) =>
  request("POST", path, parse, body);
const del = <T>(path: string, parse: (d: unknown) => T) => request("DELETE", path, parse);

export const api = {
  auth: {
    /** Resolves to null when there is no valid session. */
    me: async (): Promise<AuthSession | null> => {
      try {
        return await get("/v1/auth/me", parseSession);
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
      }
    },
    signup: (body: SignupRequest) => post("/v1/auth/signup", body, parseSession),
    login: (body: LoginRequest) => post("/v1/auth/login", body, parseSession),
    logout: async () => {
      const result = await post("/v1/auth/logout", {}, (d) => okSchema.parse(d));
      forgetSession();
      return result;
    },
    sessions: (): Promise<SessionList> =>
      get("/v1/auth/sessions", (d) => sessionListSchema.parse(d)),
    revokeSession: (id: string) => del(`/v1/auth/sessions/${id}`, (d) => okSchema.parse(d)),
    mfa: {
      verify: (body: MfaVerifyRequest) => post("/v1/auth/mfa/verify", body, parseSession),
      enroll: (): Promise<TotpEnrollResponse> =>
        post("/v1/auth/mfa/totp/enroll", {}, (d) => totpEnrollResponseSchema.parse(d)),
      confirm: (code: string): Promise<TotpConfirmResponse> =>
        post("/v1/auth/mfa/totp/confirm", { code }, (d) => totpConfirmResponseSchema.parse(d)),
      disable: (body: TotpDisableRequest) =>
        post("/v1/auth/mfa/totp/disable", body, (d) => okSchema.parse(d)),
    },
    passkeys: {
      list: (): Promise<PasskeyList> => get("/v1/auth/passkeys", (d) => passkeyListSchema.parse(d)),
      registerOptions: (): Promise<WebauthnOptions> =>
        post("/v1/auth/passkeys/register/options", {}, (d) => webauthnOptionsSchema.parse(d)),
      registerVerify: (name: string, response: WebauthnResponse): Promise<PasskeySummary> =>
        post("/v1/auth/passkeys/register/verify", { name, response }, (d) =>
          passkeySummarySchema.parse(d),
        ),
      remove: (id: string) => del(`/v1/auth/passkeys/${id}`, (d) => okSchema.parse(d)),
      loginOptions: (): Promise<WebauthnOptions> =>
        post("/v1/auth/passkeys/login/options", {}, (d) => webauthnOptionsSchema.parse(d)),
      loginVerify: (response: WebauthnResponse) =>
        post("/v1/auth/passkeys/login/verify", { response }, parseSession),
    },
    email: {
      verify: (token: string) => post("/v1/auth/email/verify", { token }, (d) => okSchema.parse(d)),
      resend: () =>
        post("/v1/auth/email/resend", {}, (d) => z.object({ sent: z.boolean() }).parse(d)),
    },
    password: {
      forgot: (email: string) =>
        post("/v1/auth/password/forgot", { email }, (d) => okSchema.parse(d)),
      reset: (token: string, password: string) =>
        post("/v1/auth/password/reset", { token, password }, (d) => okSchema.parse(d)),
      change: (currentPassword: string, newPassword: string) =>
        post("/v1/auth/password/change", { currentPassword, newPassword }, (d) =>
          okSchema.parse(d),
        ),
    },
  },
  invites: {
    list: (): Promise<InviteList> => get("/v1/invites", (d) => inviteListSchema.parse(d)),
    create: (body: InviteCreateRequest): Promise<InviteCreated> =>
      post("/v1/invites", body, (d) => inviteCreatedSchema.parse(d)),
    revoke: (id: string) => del(`/v1/invites/${id}`, (d) => okSchema.parse(d)),
    resend: (id: string): Promise<InviteCreated> =>
      post(`/v1/invites/${id}/resend`, {}, (d) => inviteCreatedSchema.parse(d)),
    preview: (token: string): Promise<InvitePreview> =>
      get(`/v1/invites/${encodeURIComponent(token)}`, (d) => invitePreviewSchema.parse(d)),
    accept: (token: string, body: InviteAcceptRequest) =>
      post(`/v1/invites/${encodeURIComponent(token)}/accept`, body, (d) =>
        authSessionSchema.parse(d),
      ),
  },
  audit: {
    list: (query: Partial<AuditQuery>): Promise<AuditList> => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== "") params.set(key, String(value));
      }
      const suffix = params.size > 0 ? `?${params.toString()}` : "";
      return get(`/v1/audit${suffix}`, (d) => auditListSchema.parse(d));
    },
    exportUrl: () => `${API_BASE}/v1/audit/export.csv`,
  },
  users: {
    list: (): Promise<UserList> => get("/v1/users", (d) => userListSchema.parse(d)),
    changeRole: (id: string, role: UserRole) =>
      request("PATCH", `/v1/users/${id}/role`, (d) => okSchema.parse(d), { role }),
    suspend: (id: string) => post(`/v1/users/${id}/suspend`, {}, (d) => okSchema.parse(d)),
    reactivate: (id: string) => post(`/v1/users/${id}/reactivate`, {}, (d) => okSchema.parse(d)),
    signOutEverywhere: (id: string) =>
      post(`/v1/users/${id}/sessions/revoke`, {}, (d) => okSchema.parse(d)),
    resetMfa: (id: string) => post(`/v1/users/${id}/mfa/reset`, {}, (d) => okSchema.parse(d)),
    sendPasswordReset: (id: string) =>
      post(`/v1/users/${id}/password-reset`, {}, (d) => okSchema.parse(d)),
  },
  features: (): Promise<Features> => get("/v1/features", (d) => featuresSchema.parse(d)),
  workspace: {
    exportUrl: () => `${API_BASE}/v1/workspace/export`,
    accountExportUrl: () => `${API_BASE}/v1/account/export`,
    close: (body: WorkspaceCloseRequest): Promise<WorkspaceClosed> =>
      post("/v1/workspace/close", body, (d) => workspaceClosedSchema.parse(d)),
    rename: (name: string): Promise<{ name: string }> =>
      request("PATCH", "/v1/workspace", (d) => z.object({ name: z.string() }).parse(d), { name }),
  },
  billing: {
    entitlements: (): Promise<Entitlements> =>
      get("/v1/billing/entitlements", (d) => entitlementsSchema.parse(d)),
    summary: (): Promise<BillingSummary> =>
      get("/v1/billing", (d) => billingSummarySchema.parse(d)),
    choosePlan: (body: BillingPlanRequest): Promise<BillingRedirect> =>
      post("/v1/billing/plan", body, (d) => billingRedirectSchema.parse(d)),
    portal: (): Promise<{ url: string }> =>
      post("/v1/billing/portal", {}, (d) => z.object({ url: z.string() }).parse(d)),
  },
  mailboxes: {
    list: (): Promise<MailboxList> => get("/v1/mailboxes", (d) => mailboxListSchema.parse(d)),
    create: (body: MailboxCreateRequest): Promise<MailboxCreated> =>
      post("/v1/mailboxes", body, (d) => mailboxCreatedSchema.parse(d)),
    resetPassword: (id: string): Promise<{ appPassword: string }> =>
      post(`/v1/mailboxes/${id}/password`, {}, (d) =>
        z.object({ appPassword: z.string() }).parse(d),
      ),
    remove: (id: string) => del(`/v1/mailboxes/${id}`, (d) => okSchema.parse(d)),
    addAlias: (id: string, body: AliasCreateRequest): Promise<Mailbox> =>
      post(
        `/v1/mailboxes/${id}/aliases`,
        body,
        (d) => z.object({ mailbox: mailboxSchema }).parse(d).mailbox,
      ),
    removeAlias: (id: string, aliasId: string): Promise<Mailbox> =>
      del(
        `/v1/mailboxes/${id}/aliases/${aliasId}`,
        (d) => z.object({ mailbox: mailboxSchema }).parse(d).mailbox,
      ),
  },
  mailGroups: {
    list: (): Promise<MailGroupList> => get("/v1/mail-groups", (d) => mailGroupListSchema.parse(d)),
    create: (body: MailGroupCreateRequest): Promise<MailGroup> =>
      post("/v1/mail-groups", body, (d) => mailGroupSchema.parse(d)),
    update: (id: string, body: MailGroupUpdateRequest): Promise<MailGroup> =>
      request("PATCH", `/v1/mail-groups/${id}`, (d) => mailGroupSchema.parse(d), body),
    remove: (id: string) => del(`/v1/mail-groups/${id}`, (d) => okSchema.parse(d)),
  },
  mail: {
    account: (): Promise<MailAccount> => get("/v1/mail/account", (d) => mailAccountSchema.parse(d)),
    folders: (): Promise<MailFolderList> =>
      get("/v1/mail/folders", (d) => mailFolderListSchema.parse(d)),
    emails: (mailboxId: string, position: number, limit: number, q = ""): Promise<EmailPage> =>
      get(
        `/v1/mail/emails?mailboxId=${encodeURIComponent(mailboxId)}&position=${position}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
        (d) => emailPageSchema.parse(d),
      ),
    attachmentUrl: (id: string, blobId: string, name: string | null) =>
      `${API_BASE}/v1/mail/emails/${encodeURIComponent(id)}/attachments/${encodeURIComponent(blobId)}${name ? `?name=${encodeURIComponent(name)}` : ""}`,
    eventsUrl: () => `${API_BASE}/v1/mail/events`,
    email: (id: string): Promise<EmailDetail> =>
      get(`/v1/mail/emails/${encodeURIComponent(id)}`, (d) => emailDetailSchema.parse(d)),
    update: (id: string, change: EmailUpdateRequest) =>
      request(
        "PATCH",
        `/v1/mail/emails/${encodeURIComponent(id)}`,
        (d) => okSchema.parse(d),
        change,
      ),
    updateMany: (body: EmailBatchUpdateRequest) =>
      post("/v1/mail/emails/batch", body, (d) => okSchema.parse(d)),
    send: (body: SendEmailRequest): Promise<SentEmail> =>
      post("/v1/mail/send", body, (d) => sentEmailSchema.parse(d)),
    undoSend: (submissionId: string) =>
      post(`/v1/mail/send/${encodeURIComponent(submissionId)}/undo`, {}, (d) =>
        undoSendResultSchema.parse(d),
      ),
    saveDraft: (draft: SaveDraftRequest) =>
      request("PUT", "/v1/mail/drafts", (d) => savedDraftSchema.parse(d), draft),
    discardDraft: (id: string) =>
      del(`/v1/mail/drafts/${encodeURIComponent(id)}`, (d) => okSchema.parse(d)),
    settings: (): Promise<MailSettings> =>
      get("/v1/mail/settings", (d) => mailSettingsSchema.parse(d)),
    updateSettings: (settings: MailSettings): Promise<MailSettings> =>
      request("PUT", "/v1/mail/settings", (d) => mailSettingsSchema.parse(d), settings),
    /** Sends the file itself as the request body. */
    upload: async (file: File): Promise<UploadedAttachment> => {
      const headers: Record<string, string> = {
        "content-type": file.type || "application/octet-stream",
      };
      if (csrfToken) headers["x-csrf-token"] = csrfToken;
      const response = await fetch(
        `${API_BASE}/v1/mail/uploads?name=${encodeURIComponent(file.name)}`,
        { method: "POST", credentials: "include", headers, body: file },
      ).catch(() => {
        throw new ApiRequestError(0, "offline", "The server could not be reached");
      });
      const data: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const error = (data as { error?: { code?: string; message?: string } } | null)?.error;
        throw new ApiRequestError(
          response.status,
          error?.code ?? (response.status === 413 ? "too_large" : "request_failed"),
          error?.message ?? response.statusText,
        );
      }
      return uploadedAttachmentSchema.parse(data);
    },
    devices: (): Promise<MailClientSettings> =>
      get("/v1/mail/devices", (d) => mailClientSettingsSchema.parse(d)),
    appleProfileUrl: () => `${API_BASE}/v1/mail/devices/apple.mobileconfig`,
    appPassword: (currentPassword: string) =>
      post("/v1/mail/app-password", { currentPassword }, (d) => appPasswordSchema.parse(d)),
    recipients: (q: string): Promise<RecipientSuggestionList> =>
      get(`/v1/mail/recipients?q=${encodeURIComponent(q)}`, (d) =>
        recipientSuggestionListSchema.parse(d),
      ),
    ai: (id: string, body: AiMailRequest): Promise<AiMailResult> =>
      post(`/v1/mail/emails/${encodeURIComponent(id)}/ai`, body, (d) =>
        aiMailResultSchema.parse(d),
      ),
  },
  calendar: {
    calendars: (): Promise<CalendarList> =>
      get("/v1/calendar/calendars", (d) => calendarListSchema.parse(d)),
    events: (from: string, to: string, timeZone: string): Promise<CalendarEventList> =>
      get(
        `/v1/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&timeZone=${encodeURIComponent(timeZone)}`,
        (d) => calendarEventListSchema.parse(d),
      ),
    create: (input: CalendarEventInput): Promise<CalendarEvent> =>
      post("/v1/calendar/events", input, (d) => calendarEventSchema.parse(d)),
    update: (id: string, patch: CalendarEventPatch): Promise<CalendarEvent> =>
      request(
        "PATCH",
        `/v1/calendar/events/${encodeURIComponent(id)}`,
        (d) => calendarEventSchema.parse(d),
        patch,
      ),
    remove: (id: string) =>
      del(`/v1/calendar/events/${encodeURIComponent(id)}`, (d) => okSchema.parse(d)),
  },
  push: {
    config: (): Promise<PushConfig> => get("/v1/push/config", (d) => pushConfigSchema.parse(d)),
    devices: () => get("/v1/push/devices", (d) => pushDevicesSchema.parse(d)),
    subscribe: (input: PushSubscriptionInput) =>
      post("/v1/push/subscriptions", input, (d) => pushDevicesSchema.parse(d)),
    unsubscribe: (endpoint: string) =>
      request("DELETE", "/v1/push/subscriptions", (d) => pushDevicesSchema.parse(d), { endpoint }),
    test: () => post("/v1/push/test", {}, (d) => pushTestResultSchema.parse(d)),
  },
  notes: {
    notebooks: (): Promise<NotebookList> =>
      get("/v1/notes/notebooks", (d) => notebookListSchema.parse(d)),
    createNotebook: (name: string): Promise<Notebook> =>
      post("/v1/notes/notebooks", { name }, (d) => notebookSchema.parse(d)),
    tags: (): Promise<NoteTagList> => get("/v1/notes/tags", (d) => noteTagListSchema.parse(d)),
    list: (query: NoteQuery): Promise<NoteList> => {
      const params = new URLSearchParams(
        Object.entries(query).filter((entry): entry is [string, string] => Boolean(entry[1])),
      ).toString();
      return get(`/v1/notes${params ? `?${params}` : ""}`, (d) => noteListSchema.parse(d));
    },
    get: (id: string): Promise<Note> =>
      get(`/v1/notes/${encodeURIComponent(id)}`, (d) => noteSchema.parse(d)),
    /** Omitted fields take their defaults on the server. */
    create: (input: Partial<NoteInput>): Promise<Note> =>
      post("/v1/notes", input, (d) => noteSchema.parse(d)),
    update: (id: string, patch: NotePatch): Promise<Note> =>
      request("PATCH", `/v1/notes/${encodeURIComponent(id)}`, (d) => noteSchema.parse(d), patch),
    remove: (id: string) => del(`/v1/notes/${encodeURIComponent(id)}`, (d) => okSchema.parse(d)),
    share: (id: string, userId: string, permission: NotePermission) =>
      request(
        "PUT",
        `/v1/notes/${encodeURIComponent(id)}/shares`,
        (d) => z.object({ shares: z.array(noteShareSchema) }).parse(d),
        { userId, permission },
      ),
    unshare: (id: string, userId: string) =>
      del(`/v1/notes/${encodeURIComponent(id)}/shares/${encodeURIComponent(userId)}`, (d) =>
        z.object({ shares: z.array(noteShareSchema) }).parse(d),
      ),
  },
  /** Colleagues in the workspace, for sharing. */
  people: (q: string) => get(`/v1/people?q=${encodeURIComponent(q)}`, (d) => peopleSchema.parse(d)),
  tasks: {
    lists: (): Promise<TaskListsResponse> =>
      get("/v1/tasks/lists", (d) => taskListsResponseSchema.parse(d)),
    createList: (name: string): Promise<TaskList> =>
      post("/v1/tasks/lists", { name }, (d) => taskListSchema.parse(d)),
    list: (listId?: string): Promise<TasksResponse> =>
      get(`/v1/tasks${listId ? `?listId=${encodeURIComponent(listId)}` : ""}`, (d) =>
        tasksResponseSchema.parse(d),
      ),
    /** Omitted optional fields take their defaults on the server. */
    create: (
      input: Pick<TaskInput, "listId" | "title" | "timeZone"> & Partial<TaskInput>,
    ): Promise<Task> => post("/v1/tasks", input, (d) => taskSchema.parse(d)),
    update: (id: string, patch: TaskPatch): Promise<Task> =>
      request("PATCH", `/v1/tasks/${encodeURIComponent(id)}`, (d) => taskSchema.parse(d), patch),
    reorder: (ids: string[]) => post("/v1/tasks/reorder", { ids }, (d) => okSchema.parse(d)),
    remove: (id: string) => del(`/v1/tasks/${encodeURIComponent(id)}`, (d) => okSchema.parse(d)),
  },
  contacts: {
    books: (): Promise<AddressBookList> =>
      get("/v1/contacts/books", (d) => addressBookListSchema.parse(d)),
    list: (q = ""): Promise<ContactList> =>
      get(`/v1/contacts${q ? `?q=${encodeURIComponent(q)}` : ""}`, (d) =>
        contactListSchema.parse(d),
      ),
    create: (input: ContactInput): Promise<Contact> =>
      post("/v1/contacts", input, (d) => contactSchema.parse(d)),
    update: (id: string, patch: ContactPatch): Promise<Contact> =>
      request(
        "PATCH",
        `/v1/contacts/${encodeURIComponent(id)}`,
        (d) => contactSchema.parse(d),
        patch,
      ),
    remove: (id: string) => del(`/v1/contacts/${encodeURIComponent(id)}`, (d) => okSchema.parse(d)),
  },
  domains: {
    check: (body: DomainCheckRequest): Promise<DomainCheckResponse> =>
      post("/v1/domains/check", body, (d) => domainCheckResponseSchema.parse(d)),
    owned: (): Promise<OwnedDomainList> =>
      get("/v1/domains", (d) => ownedDomainListSchema.parse(d)),
    renewQuote: (id: string, years: number): Promise<DomainRenewQuote> =>
      get(`/v1/domains/${id}/renew/quote?years=${years}`, (d) => domainRenewQuoteSchema.parse(d)),
    renew: (id: string, years: number): Promise<OrderCreated> =>
      post(`/v1/domains/${id}/renew`, { years }, (d) => orderCreatedSchema.parse(d)),
    setAutoRenew: (id: string, autoRenew: boolean) =>
      request("PATCH", `/v1/domains/${id}`, (d) => okSchema.parse(d), { autoRenew }),
    detail: (id: string): Promise<OwnedDomainDetail> =>
      get(`/v1/domains/${id}`, (d) => ownedDomainDetailSchema.parse(d)),
    updateSettings: (id: string, change: DomainSettingsRequest) =>
      request("PATCH", `/v1/domains/${id}/settings`, (d) => okSchema.parse(d), change),
    setNameservers: (id: string, nameservers: string[]) =>
      request("PUT", `/v1/domains/${id}/nameservers`, (d) => okSchema.parse(d), { nameservers }),
    setupEmail: (id: string): Promise<EmailSetupApplyResponse> =>
      post(`/v1/domains/${id}/email-setup`, {}, (d) => emailSetupApplyResponseSchema.parse(d)),
    authCode: (id: string): Promise<DomainAuthCode> =>
      get(`/v1/domains/${id}/auth-code`, (d) => domainAuthCodeSchema.parse(d)),
    connect: (name: string): Promise<OwnedDomain> =>
      post("/v1/domains/connect", { name }, (d) => ownedDomainSchema.parse(d)),
    verification: (id: string): Promise<DomainVerification> =>
      get(`/v1/domains/${id}/verification`, (d) => domainVerificationSchema.parse(d)),
    verify: (id: string): Promise<DomainVerification> =>
      post(`/v1/domains/${id}/verify`, {}, (d) => domainVerificationSchema.parse(d)),
    emailDnsCheck: (id: string): Promise<EmailDnsCheck> =>
      get(`/v1/domains/${id}/email-dns-check`, (d) => emailDnsCheckSchema.parse(d)),
    disconnect: (id: string) =>
      request("DELETE", `/v1/domains/${id}/connection`, (d) => okSchema.parse(d)),
  },
  orders: {
    quote: (body: OrderQuoteRequest): Promise<OrderQuote> =>
      post("/v1/orders/quote", body, (d) => orderQuoteSchema.parse(d)),
    create: (body: OrderCreateRequest): Promise<OrderCreated> =>
      post("/v1/orders", body, (d) => orderCreatedSchema.parse(d)),
    list: (): Promise<OrderList> => get("/v1/orders", (d) => orderListSchema.parse(d)),
    get: (id: string): Promise<Order> => get(`/v1/orders/${id}`, (d) => orderSchema.parse(d)),
  },
};
